import { reschedulableProcedureStatuses, DraftWaypointStatus } from '@caresend/types';
import { formatAddressUS, AddressFormatType, objectFilter, hasStatus } from '@caresend/utils';
import { getBrickAndMortarLabs, getBrickAndMortarLab } from '../../../../components/specific/schedulingFlow/BrickAndMortar/helpers.js';
import { formatDateForRescheduling } from '../../../../components/specific/schedulingFlow/DateTime/helpers.js';
import { getStore } from '../../../helpers/initStore.js';
import { useDraftWaypointsStore } from '../../../pinia/draftWaypoints/index.js';

/**
 * Copies pieces of data from the original draft waypoint to be displayed
 * for comparison to the user.
 */
const buildOldDraftWaypoint = (draftWaypoint) => {
    const store = getStore();
    const newID = store.state.schedulingFlow.localState.newDraftWaypointID;
    if (!newID)
        throw Error('Failed to initialize new draft waypoint.');
    return {
        ...draftWaypoint,
        rescheduledToID: newID,
    };
};
/**
 * Creates a new draft waypoint based on the original draft waypoint. The
 * scheduling flow must locally initialize a new draft waypoint ID and store it
 * prior to calling this function. This draft waypoint won't exist in the db
 * until the flow is finalized.
 */
const buildNewDraftWaypoint = (draftWaypoint, proceduresIneligibleForRescheduling) => {
    const store = getStore();
    const newID = store.state.schedulingFlow.localState.newDraftWaypointID;
    if (!newID)
        throw Error('Failed to initialize new draft waypoint.');
    const procedures = (proceduresIneligibleForRescheduling === null || proceduresIneligibleForRescheduling === void 0 ? void 0 : proceduresIneligibleForRescheduling.length)
        ? objectFilter(draftWaypoint.procedures, (_, procedureID) => !proceduresIneligibleForRescheduling.includes(procedureID))
        : draftWaypoint.procedures;
    return {
        ...draftWaypoint,
        id: newID,
        drawPlaceID: undefined,
        location: undefined,
        finalizedDate: undefined,
        status: DraftWaypointStatus.PENDING,
        rescheduledFromID: draftWaypoint.id,
        procedures,
    };
};
const setDataForRescheduling = (draftWaypoint, proceduresIneligibleForRescheduling) => {
    const store = getStore();
    store.commit('schedulingFlow/SET_OLD_DRAFT_WAYPOINT', buildOldDraftWaypoint(draftWaypoint));
    store.commit('schedulingFlow/SET_DRAFT_WAYPOINT', buildNewDraftWaypoint(draftWaypoint, proceduresIneligibleForRescheduling));
    store.commit('schedulingFlow/SET_PRIMARY_CONTACT', draftWaypoint.contact);
};
/**
 * Gets the original draft waypoint for the visit page for a cancelled visit.
 * This is the same draft waypoint that is passed in.
 */
const getOldDraftWaypointForCanceledVisit = (visitPageCanceledDraftWaypointID) => useDraftWaypointsStore().getters.getDenormalizedDraftWaypoint(visitPageCanceledDraftWaypointID);
/**
 * Gets the original draft waypoint for the scheduling flow while rescheduling.
 */
const getOldDraftWaypointDuringRescheduling = () => getStore().state.schedulingFlow.localState.oldDraftWaypoint;
/**
 * Gets the new draft waypoint for the visit page for a cancelled visit.
 * This will be a different draft waypoint than the one passed in. Both
 * draft waypoints must have been fetched prior to calling this function.
 */
const getNewDraftWaypointForCanceledVisit = (visitPageCanceledDraftWaypointID) => {
    const { getters } = useDraftWaypointsStore();
    const canceledDraftWaypoint = getters.getDenormalizedDraftWaypoint(visitPageCanceledDraftWaypointID);
    const { rescheduledToID } = canceledDraftWaypoint !== null && canceledDraftWaypoint !== void 0 ? canceledDraftWaypoint : {};
    if (!rescheduledToID)
        return undefined;
    return getters.getDenormalizedDraftWaypoint(rescheduledToID);
};
/**
 * Gets the new draft waypoint for the scheduling flow while rescheduling.
 */
const getNewDraftWaypointDuringRescheduling = () => getStore().state.schedulingFlow.draftWaypoint;
const getOldIsBrickAndMortar = (visitPageCanceledDraftWaypointID) => {
    const isRescheduling = !visitPageCanceledDraftWaypointID;
    const draftWaypoint = isRescheduling
        ? getOldDraftWaypointDuringRescheduling()
        : getOldDraftWaypointForCanceledVisit(visitPageCanceledDraftWaypointID);
    return !!(draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.drawPlaceID);
};
const getNewIsBrickAndMortar = (visitPageCanceledDraftWaypointID) => {
    const isRescheduling = !visitPageCanceledDraftWaypointID;
    if (isRescheduling) {
        const store = getStore();
        return store.state.schedulingFlow.localState.isBrickAndMortarVisit;
    }
    const newDraftWaypoint = getNewDraftWaypointForCanceledVisit(visitPageCanceledDraftWaypointID);
    return !!(newDraftWaypoint === null || newDraftWaypoint === void 0 ? void 0 : newDraftWaypoint.drawPlaceID);
};
const getLabLabelByPlaceID = (drawPlaceID, draftWaypointForVisitPage) => {
    var _a;
    const isRescheduling = !draftWaypointForVisitPage;
    let lab;
    if (isRescheduling) {
        lab = (_a = getBrickAndMortarLabs([drawPlaceID])[0]) !== null && _a !== void 0 ? _a : undefined;
    }
    else {
        const { facilityDetails, location } = draftWaypointForVisitPage;
        if (!facilityDetails)
            return 'Unknown';
        const { name, phoneNumber } = facilityDetails !== null && facilityDetails !== void 0 ? facilityDetails : {};
        lab = getBrickAndMortarLab(facilityDetails, name, phoneNumber, location);
    }
    if (!lab)
        return 'Unknown';
    const { name, location } = lab;
    const shortAddress = formatAddressUS(location, undefined, AddressFormatType.SHORT_NO_STATE);
    return `${name} (${shortAddress})`;
};
const getOldLabLabel = (visitPageCanceledDraftWaypointID) => {
    const isRescheduling = !visitPageCanceledDraftWaypointID;
    let drawPlaceID;
    let visitPageCanceledDraftWaypoint;
    if (isRescheduling) {
        const oldDraftWaypoint = getOldDraftWaypointDuringRescheduling();
        drawPlaceID = oldDraftWaypoint === null || oldDraftWaypoint === void 0 ? void 0 : oldDraftWaypoint.drawPlaceID;
    }
    else {
        const oldDraftWaypoint = getOldDraftWaypointForCanceledVisit(visitPageCanceledDraftWaypointID);
        visitPageCanceledDraftWaypoint = oldDraftWaypoint;
        drawPlaceID = oldDraftWaypoint === null || oldDraftWaypoint === void 0 ? void 0 : oldDraftWaypoint.drawPlaceID;
    }
    return drawPlaceID
        ? getLabLabelByPlaceID(drawPlaceID, visitPageCanceledDraftWaypoint)
        : undefined;
};
const getNewLabLabel = (visitPageCanceledDraftWaypointID) => {
    const isRescheduling = !visitPageCanceledDraftWaypointID;
    let drawPlaceID;
    let visitPageNewDraftWaypoint;
    if (isRescheduling) {
        drawPlaceID = getStore().state.schedulingFlow.selectedDrawPlaceID;
    }
    else {
        const newDraftWaypoint = getNewDraftWaypointForCanceledVisit(visitPageCanceledDraftWaypointID);
        visitPageNewDraftWaypoint = newDraftWaypoint;
        drawPlaceID = newDraftWaypoint === null || newDraftWaypoint === void 0 ? void 0 : newDraftWaypoint.drawPlaceID;
    }
    return drawPlaceID
        ? getLabLabelByPlaceID(drawPlaceID, visitPageNewDraftWaypoint)
        : undefined;
};
const getOldDateLabel = (visitPageCanceledDraftWaypointID) => {
    var _a;
    const isRescheduling = !visitPageCanceledDraftWaypointID;
    let date;
    if (isRescheduling) {
        const oldDraftWaypoint = getOldDraftWaypointDuringRescheduling();
        date = (_a = oldDraftWaypoint === null || oldDraftWaypoint === void 0 ? void 0 : oldDraftWaypoint.finalizedDate) === null || _a === void 0 ? void 0 : _a.date;
    }
    else {
        const oldDraftWaypoint = getOldDraftWaypointForCanceledVisit(visitPageCanceledDraftWaypointID);
        date = oldDraftWaypoint === null || oldDraftWaypoint === void 0 ? void 0 : oldDraftWaypoint.date;
    }
    return date ? formatDateForRescheduling(date) : undefined;
};
const getNewDateLabel = (visitPageCanceledDraftWaypointID) => {
    var _a;
    const isRescheduling = !visitPageCanceledDraftWaypointID;
    let date;
    if (isRescheduling) {
        const newDraftWaypoint = getNewDraftWaypointDuringRescheduling();
        date = (_a = newDraftWaypoint === null || newDraftWaypoint === void 0 ? void 0 : newDraftWaypoint.finalizedDate) === null || _a === void 0 ? void 0 : _a.date;
    }
    else {
        const newDraftWaypoint = getNewDraftWaypointForCanceledVisit(visitPageCanceledDraftWaypointID);
        date = newDraftWaypoint === null || newDraftWaypoint === void 0 ? void 0 : newDraftWaypoint.date;
    }
    return date ? formatDateForRescheduling(date) : undefined;
};
/**
 * Returns title and info about the original visit for rescheduling.
 * Examples:
 * - { title: "Original date and time", info: "Friday, Dec 6 | 10 - 11 am" }
 * - { title: "Original location", info: "Quest Diagnostics (2401 Dunleer Pl, Los Angeles)" }
 *
 * If an ID is passed, info is sourced from pinia for the visit page. If not,
 * it is sourced from the scheduling flow module for rescheduling.
 */
const getOldReschedulingInfo = (
/** If currently rescheduling, do not pass. */
visitPageCanceledDraftWaypointID) => {
    var _a, _b;
    const isBrickAndMortar = getOldIsBrickAndMortar(visitPageCanceledDraftWaypointID);
    if (isBrickAndMortar) {
        return {
            title: 'Original location',
            info: (_a = getOldLabLabel(visitPageCanceledDraftWaypointID)) !== null && _a !== void 0 ? _a : 'Unknown',
        };
    }
    return {
        title: 'Original date and time',
        info: (_b = getOldDateLabel(visitPageCanceledDraftWaypointID)) !== null && _b !== void 0 ? _b : 'Unknown',
    };
};
/**
 * Returns title and info tuple about the new visit for rescheduling.
 * Examples:
 * - { title: "New date and time", info: "Friday, Dec 6 | 10 - 11 am" }
 * - { title: "New location", info: "Quest Diagnostics (2401 Dunleer Pl, Los Angeles)" }
 *
 * If an ID is passed, info is sourced from pinia for the visit page. If not,
 * it is sourced from the scheduling flow module for rescheduling.
 */
const getNewReschedulingInfo = (
/** If currently rescheduling, do not pass. */
visitPageCanceledDraftWaypointID) => {
    var _a, _b;
    const isBrickAndMortar = getNewIsBrickAndMortar(visitPageCanceledDraftWaypointID);
    if (isBrickAndMortar) {
        return {
            title: 'New location',
            info: (_a = getNewLabLabel(visitPageCanceledDraftWaypointID)) !== null && _a !== void 0 ? _a : 'Unknown',
        };
    }
    return {
        title: 'New date and time',
        info: (_b = getNewDateLabel(visitPageCanceledDraftWaypointID)) !== null && _b !== void 0 ? _b : 'Unknown',
        /** We won't show this for ES visits (as of Jan 26th) */
        // subtitle: 'We’ll send an ETA on the day of the appointment.',
    };
};
/**
 * Returns the filtered procedure data for the reschedulable procedures of a
 * draft waypoint. If no specific canceled procedures are passed in, all
 * procedures are returned.
 */
const getReschedulableProcedureData = (procedureData, proceduresIneligibleForRescheduling) => {
    if (!proceduresIneligibleForRescheduling)
        return procedureData;
    return objectFilter(procedureData, (procedure) => !proceduresIneligibleForRescheduling.includes(procedure.id));
};
/**
 * Returns the procedure IDs of the canceled or failed procedures on a draft
 * waypoint that are being rescheduled.
 */
const getCanceledProceduresBeingRescheduled = (draftWaypoint, procedures) => Object.keys(draftWaypoint.procedures).filter((procedureID) => hasStatus(procedures[procedureID], reschedulableProcedureStatuses));

export { getCanceledProceduresBeingRescheduled, getNewDraftWaypointForCanceledVisit, getNewReschedulingInfo, getOldReschedulingInfo, getReschedulableProcedureData, setDataForRescheduling };
